import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BarLoading } from "@/components/Loading/BarLoading";
import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { useOrgChannels } from "@/features/channels/useOrgChannels";
import { interlude } from "@/interlude";
import { ECOMMERCE_PLATFORMS } from "@/pages/applications/ecommercePlatforms";
import { ChannelIcon } from "@/pages/channels/components/ChannelIcon";
import { ChannelStatus } from "@/pages/channels/components/ChannelStatus";

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    margin: "0px",
    padding: "0px",
    listStyle: "none",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "12px 20px",
    "&:not(:last-of-type)": {
      borderBottom: `1px solid ${theme.colors.staticFgLine}`,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const ChannelSources: React.FC = function ChannelSources() {
  const { t } = useTranslation();
  const orgChannels = useOrgChannels();

  return (
    <Box component="section" sx={styles.section}>
      <Typography variant="h2">
        {t("dataHub.sources.channels.title", "Channels")}
      </Typography>
      {!orgChannels.isSuccess ? (
        orgChannels.isPending ? (
          <BarLoading />
        ) : null
      ) : orgChannels.channels.length === 0 ? (
        <Typography variant="body">
          {t("dataHub.sources.channels.empty", "No channels connected yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={styles.list}>
          {orgChannels.channels.map((channel) => (
            <Box
              component="li"
              key={`${channel.type}-${channel.id}`}
              sx={styles.row}
            >
              <Stack direction="row" gap="10px" alignItems="center">
                <ChannelIcon
                  type={channel.type}
                  isWebStream={
                    channel.type === "wccs" &&
                    channel.channelInformation.type === "web-streams"
                  }
                />
                <Typography variant="body">{channel.name}</Typography>
              </Stack>
              <ChannelStatus status={channel.status} type={channel.type} />
            </Box>
          ))}
        </Box>
      )}
      <Box>
        <RouterLink to="/channels">
          {t("dataHub.sources.channels.manage", "Manage channels")}
        </RouterLink>
      </Box>
    </Box>
  );
};

const EcommerceSources: React.FC = function EcommerceSources() {
  const { t } = useTranslation();
  const hasEcModuleQuery = interlude.organization.useHasEcModule();
  const connected = useMemo(() => {
    const types = hasEcModuleQuery.data?.ecPlatforms ?? [];
    return types.map(
      (type) =>
        ECOMMERCE_PLATFORMS.find((platform) => platform.type === type)?.name ??
        type,
    );
  }, [hasEcModuleQuery.data?.ecPlatforms]);

  // Older interlude responses have no platform list; say nothing rather than
  // claim the org has none.
  if (hasEcModuleQuery.isSuccess && !hasEcModuleQuery.data.ecPlatforms)
    return null;

  return (
    <Box component="section" sx={styles.section}>
      <Typography variant="h2">
        {t("dataHub.sources.ecommerce.title", "E-commerce platforms")}
      </Typography>
      {!hasEcModuleQuery.isSuccess ? (
        hasEcModuleQuery.isPending ? (
          <BarLoading />
        ) : null
      ) : connected.length === 0 ? (
        <Typography variant="body">
          {t(
            "dataHub.sources.ecommerce.empty",
            "No e-commerce platforms connected yet.",
          )}
        </Typography>
      ) : (
        <Box component="ul" sx={styles.list}>
          {connected.map((name) => (
            <Box component="li" key={name} sx={styles.row}>
              <Typography variant="body">{name}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box>
        <RouterLink to="/applications">
          {t("dataHub.sources.ecommerce.manage", "Manage integrations")}
        </RouterLink>
      </Box>
    </Box>
  );
};

/**
 * Where the org's customer data comes from: its channels and the e-commerce
 * platforms MAAC syncs orders from.
 */
export const DataHubSourcesTab: React.FC = function DataHubSourcesTab() {
  return (
    <Stack gap="32px">
      <ChannelSources />
      <EcommerceSources />
    </Stack>
  );
};
